import React, { useCallback, useMemo } from "react";
import styled from "styled-components";
import { useHistory } from "react-router-dom";

import Typography from "antd/es/typography";
import Divider from "antd/es/divider";
import Tag from "antd/es/tag";
import Tooltip from "antd/es/tooltip";
import List from "antd/es/list";

import doorState from "config/doorState";
import colors from "config/colors";
import { timeFormat, dateTimeFormat } from "config/common";
import featureToggles from "config/featureToggles";

import { StatType } from "services/stats";
import BookmarksService from "services/bookmarks";

import useStats from "hooks/useStats";
import useSearcher from "hooks/useSearcher";
import useQueryParam from "hooks/useQueryParam";
import { useLocationSelect } from "hooks/useSelect";

import MainLayout from "layouts/Main.layout";
import { Row, Col, Space } from "components/layout";
import { Text } from "components/Text";
import PageHeader from "components/MainPageHeader";
import ViewSwitcher from "components/ViewSwitcher";
import Grid from "components/Grid";
import RadioGroup from "components/RadioGroup";
import Search from "components/Search";
import FilterBar from "components/FilterBar";
import ListMode from "components/ListMode";
import Table from "components/Table";
import DropdownMenu from "components/DropdownMenu";
import { useFeatureToggleItem } from "components/FeatureToggle";

import { formatDate, formatDuration } from "helpers/date";

import StatsCard from "../components/StatsCard";
import DoorCard from "../components/DoorCard";
import DCStatsCard from "../components/DCStatsCard";
import FilterTag from "../components/FilterTag";
import StatusTag from "../components/StatusTag";


import {
  DOOR_STATE_FILTER_OPTIONS,
  DASHBOARD_FILTER_OPTIONS,
  DASHBOARD_SORT_OPTIONS,
} from "../constants";

import { filterDoorsData } from "../helpers";
import { showDropdownFilters } from "../helpers";
import { isStorageOrPallets } from "../helpers";
import { isContainer, isTrailer } from "helpers/process";

const DashboardPageHeader = styled(PageHeader)`
  & .ant-page-header-content {
    padding-top: unset;
  }
`;

const DropdownMenuWrapper = styled.div`
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
`;


const Dashboard = () => {
  const history = useHistory();
  const locationSelect = useLocationSelect();
  const individualsFeatureToggle = useFeatureToggleItem(featureToggles.FEATURE_INDIVIDUALS);
  const showIndividuals = individualsFeatureToggle?.visible ?? true;

  const [viewMode, setViewMode] = useQueryParam("view", "grid");
  const [stateTab, setStateTab] = useQueryParam("tab", "");
  const [searchQuery, setSearchQuery] = useQueryParam("s", "");
  const [progressFilter, setProgressFilter] = useQueryParam("progress", "");
  const [speedFilter, setSpeedFilter] = useQueryParam("speed", "");
  const [directionFilter, setDirectionFilter] = useQueryParam("type", "");
  const [sort, setSort] = useQueryParam("order", "");
  const [customerFilter, setCustomerFilter] = useQueryParam("customer", "");
  const [currentPage, setCurrentPage] = useQueryParam("p", 1);

  const locationsLive = useStats(
    StatType.locationsLive,
    {
      location_id: locationSelect.value,
    },
    {
      initialStats: {
        doors: [],
        summary: {},
        summaryLoading: {},
        summaryUnloading: {},
      },
      autoFetch: true,
      refreshInterval: 180000, 
    }
  );

  const searcher = useSearcher(["name"]);
  
  const stats = useMemo(() => {
    const stats = locationsLive.state.stats;
    if (!stats) return;
    const { last_update: lastUpdate } =
      stats.doors.find((d) => !!d.last_update) || {};

    return {
      doors: stats.doors,
      lastUpdate: lastUpdate
        ? formatDate(new Date(lastUpdate), timeFormat, { tzOffset: 0 })
        : null,
      summary: stats.summary || {},
      summaryLoading: stats.summary_loading || {},
      summaryUnloading: stats.summary_unloading || {},
    };
  }, [locationsLive.state.stats]);

  const customers = useMemo(() => {
    const doors = stats.doors || [];
    const activeDoors = doors.filter(d => d.door_state !== doorState.EMPTY);
    const names = Array.from(new Set(activeDoors.map(d => d.customer_name).filter(Boolean)));
    names.sort((a, b) => a.localeCompare(b));
    return names;
  }, [stats.doors]);

  // Which filters to show based on state tab
  const filterKeys = useMemo(() => (
    stateTab === doorState.NOT_STARTED
      ? ["direction", "customer"]
      : ["progress", "speed", "direction", "customer"]
  ), [stateTab]);

  const filterMenuOptions = useMemo(() => {
    const customerOptions = [
      { label: 'All', value: 'customer_' },
      ...customers.map(name => ({ label: name, value: `customer_${name}` }))
    ];
    const baseFilters = stateTab === doorState.NOT_STARTED
      ? DASHBOARD_FILTER_OPTIONS.filter(opt => opt.title === 'Process')
      : DASHBOARD_FILTER_OPTIONS;
    return [
      ...baseFilters,
      { title: 'Customer', items: customerOptions }
    ];
  }, [customers, stateTab]);

  const FILTER_OPTION_MAP = useMemo(() => ({
    progress: DASHBOARD_FILTER_OPTIONS.find(o => o.title === 'Progress'),
    speed: DASHBOARD_FILTER_OPTIONS.find(o => o.title === 'Speed'),
    direction: DASHBOARD_FILTER_OPTIONS.find(o => o.title === 'Process'),
  }), []);

  

  const filteredDoors = useMemo(() => {
    const doors = filterDoorsData(stats.doors, {
      doorState: stateTab,
      progress: progressFilter,
      speed: speedFilter,
      direction: directionFilter,
      customer: customerFilter,
      sort,
    });
    searcher.setData(doors);
    return searcher.search(searchQuery);
  }, [
    stats.doors,
    searcher,
    stateTab,
    speedFilter,
    progressFilter,
    directionFilter,
    customerFilter,
    searchQuery,
    sort,
  ]);

  const onDoorClicked = useCallback(
    (door) => {
      history.push(`/doors/${door.door_id}`, {
        ...door,
      });
    },
    [history]
  );

  const onEmployeeAvatarClicked = useCallback(
    (employee) => {
      history.push(`/individuals/${employee.employee_id}`, {
        ...employee,
      });
    },
    [history]
  );

  const toggleFavorite = useCallback(
    (door, e) => {
      e.stopPropagation();

      const bookmarks = new BookmarksService();
      const { door_id: doorId } = door;

      if (door.is_favorite === 1) {
        bookmarks.remove({ door_id: doorId });
      } else {
        bookmarks.add({ door_id: doorId });
      }

      locationsLive.store.set((state) => ({
        stats: {
          ...state.stats,
          doors: state.stats.doors.map((d) => {
            if (d.door_id === doorId) {
              d.is_favorite = d.is_favorite === 1 ? 0 : 1;
            }
            return d;
          }),
        },
      }));
    },
    [locationsLive.store]
  );

  return (
    <MainLayout>
      <DashboardPageHeader
        title="Dashboard"
        icon="grid"
        subTitle={null}
      >
        <Typography.Text
          style={{
            fontSize: "12px",
            lineHeight: "20px",
            color: "#A4B0BA",
            marginLeft: "45px",
          }}
        >
          {stats.lastUpdate ? `Refreshed at: ${stats.lastUpdate}` : ""}
        </Typography.Text>
      </DashboardPageHeader>
      <Grid gutter={24} columns={2} style={{ padding: "8px 24px 0px" }}>
        <DCStatsCard
          totalCph={[stats.summary.tot_cph, stats.summary.max_cph]}
          personCph={[
            stats.summary.avg_cph_per_employee,
            stats.summary.max_cph_per_person,
          ]}
        />
        <Grid gutter={[24, 24]} columns={3}>
          <StatsCard
            title="Not Started"
            value={stats.summary.not_started}
            valueLoads={stats.summaryLoading.not_started}
            valueUnloads={stats.summaryUnloading.not_started}
            color="#232526"
            // icon="sidebar"
            style={{
              padding: "12px",
            }}
          />
          <StatsCard
            title="In Progress"
            value={
              stats.summary.loading !== undefined &&
                stats.summary.unloading !== undefined
                ? stats.summary.loading + stats.summary.unloading
                : undefined
            }
            valueLoads={stats.summaryLoading.loading}
            valueUnloads={stats.summaryUnloading.unloading}
            // color="success"
            color="#232526"
            // icon="check-circle"
            style={{
              padding: "12px",
            }}
          />
          <StatsCard
            title="Stopped"
            value={stats.summary.stopped}
            valueLoads={stats.summaryLoading.stopped}
            valueUnloads={stats.summaryUnloading.stopped}
            // color="error"
            color="#232526"
          // icon="minus-circle"
          />
          <StatsCard
            title="Finished"
            value={stats.summary.complete}
            valueLoads={stats.summaryLoading.complete}
            valueUnloads={stats.summaryUnloading.complete}
            // color="#1560AD"
            color="#232526"
            // icon="flag"
            style={{
              padding: "12px",
            }}
          />
          <StatsCard
            title="Empty Doors"
            value={stats.summary.empty}
            valueLoads={stats.summaryLoading.empty}
            valueUnloads={stats.summaryUnloading.empty}
            color="#232526"
          // icon="disc"
          />
          <StatsCard
            title="Persons"
            value={stats.summary.employees}
            valueLoads={stats.summaryLoading.employees}
            valueUnloads={stats.summaryUnloading.employees}
            color="#232526"
          // icon="users"
          />
        </Grid>
      </Grid>

      <Row justify="center" style={{ padding: "12px 0" }}>
        <RadioGroup
          defaultValue={DOOR_STATE_FILTER_OPTIONS[0].value}
          options={DOOR_STATE_FILTER_OPTIONS}
          optionType="button"
          buttonStyle="solid"
          value={stateTab}
          onChange={(e) => {
            setStateTab(e.target.value);
            setSearchQuery("");
          }}
        />
      </Row>

      <Row style={{ padding: "12px 24px" }} align="center">
        <Col flex="1">
          <FilterBar
            title="Doors"
            titleStyles={{ fontSize: "20px", lineHeight: "28px" }}
          >
            <FilterBar.Slot align="left">
              <Space size="middle">
                <Search
                  onChange={(value) => setSearchQuery(value)}
                  value={searchQuery}
                  placeholder="Search Door Number"
                />
                {(showDropdownFilters(stateTab) || stateTab === doorState.NOT_STARTED) ? (
                  <Space size="small">
                    <DropdownMenuWrapper>
                      <DropdownMenu
                        icon={{ name: "filter", size: 16, color: "#A4B0BA", style: { marginTop: '6px' } }}
                        options={filterMenuOptions}
                        useSubmenus
                        value={
                          filterKeys.map((key) => (
                            {
                              progress: `progress_${progressFilter}`,
                              speed: `speed_${speedFilter}`,
                              direction: `direction_${directionFilter}`,
                              customer: `customer_${customerFilter}`,
                            }[key]
                          ))
                        }
                        onChange={(key) => {
                          const [, value = ""] = key.split("_");
                          if (key.startsWith("progress")) {
                            setProgressFilter(value);
                          } else if (key.startsWith("speed")) {
                            setSpeedFilter(value);
                          } else if (key.startsWith("direction")) {
                            setDirectionFilter(value);
                          } else if (key.startsWith("customer")) {
                            setCustomerFilter(value);
                          }
                        }}
                      />
                      <div>
                        {filterKeys.map((key) => {
                          const value = {
                            progress: progressFilter,
                            speed: speedFilter,
                            direction: directionFilter,
                            customer: customerFilter,
                          }[key];

                          if (value === "") return null;

                          // Build label for dynamic customer filter
                          let title, label;
                          if (key === 'customer') {
                            title = 'Customer';
                            label = value;
                          } else {
                            const opt = FILTER_OPTION_MAP[key];
                            if (!opt || !opt.title || !opt.items) return null;
                            title = opt.title;
                            const item = opt.items.find((i) => i.value === `${key}_${value}`);
                            if (!item) return null;
                            label = item.label;
                          }

                          return (
                            <FilterTag
                              style={{ marginLeft: "16px", marginRight: "0px" }}
                              key={`filter-${key}`}
                              name={title}
                              value={label}
                              closable
                              onClose={() => {
                                switch (key) {
                                  case "progress":
                                    setProgressFilter("");
                                    break;
                                  case "speed":
                                    setSpeedFilter("");
                                    break;
                                  case "direction":
                                    setDirectionFilter("");
                                    break;
                                  case "customer":
                                    setCustomerFilter("");
                                    break;

                                  default: // eslint
                                }
                              }}
                            />
                          );
                        })}
                      </div>
                    </DropdownMenuWrapper>
                    {showDropdownFilters(stateTab) && (
                      <>
                        <Divider
                          type="vertical"
                          style={{
                            height: "24px",
                            borderLeft: "1px solid #DAE0E6",
                          }}
                        />
                        <DropdownMenuWrapper>
                          <DropdownMenu
                            icon={{
                              name: "code",
                              size: [16, 25],
                              color: "#A4B0BA",
                              style: {
                                transform: "rotate(90deg)",
                                marginRight: "2px",
                              },
                            }}
                            options={DASHBOARD_SORT_OPTIONS}
                            value={sort}
                            onChange={(key) => {
                              setSort(key);
                            }}
                          />
                          <div>
                            {["progress", "speed"].map((key, i) => {
                              const [field, order] = sort.split("_");
                              if (field !== key) return null;

                              const { items } = DASHBOARD_SORT_OPTIONS[i];
                              const item = order === "desc" ? items[0] : items[1];
                              if (!item) return null;

                              return (
                                <FilterTag
                                  style={{ marginLeft: "16px", marginRight: "0px" }}
                                  key={`sort`}
                                  name={"Sort by"}
                                  value={`${{ progress: "Progress", speed: "Speed" }[key]
                                    } (${item.label})`}
                                  closable
                                  onClose={() => {
                                    setSort("");
                                  }}
                                />
                              );
                            })}
                          </div>
                        </DropdownMenuWrapper>
                      </>
                    )}
                  </Space>
                ) : null}
              </Space>
            </FilterBar.Slot>
          </FilterBar>
        </Col>
        <Col>
          <ListMode defaultValue={viewMode} onChange={setViewMode} />
        </Col>
      </Row>
      <Row style={{ padding: "12px 24px" }}>
        <Col span={24}>
          <ViewSwitcher currentKey={viewMode}>
            <ViewSwitcher.View
              viewKey={"grid"}
              loading={locationsLive.state.isLoading}
            >
              <List
                pagination={{
                  current:
                    typeof currentPage === "number"
                      ? currentPage
                      : parseInt(currentPage, 10),
                  pageSize: 10,
                  showSizeChanger: false,
                  onChange: (page) => setCurrentPage(page),
                }}
                grid={{ gutter: 24, column: 2 }}
                dataSource={filteredDoors.filter((door) => !(door.maintenance_mode === 1 && stateTab ==='IN_PROGRESS'))}
                renderItem={(door) => (
                  <List.Item key={door.door_id}>
                    <DoorCard
                      key={door.door_id}
                      hasCam={door.has_cam === 1}
                      hasAnomaly={door.has_cam === 1 && door.is_low_accuracy === 1}
                      door={door}
                      showIndividuals={showIndividuals}
                      color={door.relative_color}
                      onClick={() => onDoorClicked(door)}
                      onEmployeeAvatarClicked={onEmployeeAvatarClicked}
                      toggleFavorite={(e) => toggleFavorite(door, e)}
                    />
                  </List.Item>
                )}
              />
            </ViewSwitcher.View>
            <ViewSwitcher.View viewKey={"table"}>
              <Table
                dataSource={filteredDoors}
                rowKey="door_id"
                pagination={{
                  current:
                    typeof currentPage === "number"
                      ? currentPage
                      : parseInt(currentPage, 10),
                  pageSize: 10,
                  showSizeChanger: false,
                  onChange: (page) => setCurrentPage(page),
                }}
                scroll={{ x: 'max-content' }}
              >
                <Table.Column
                  type="link"
                  title="Door"
                  dataIndex="name"
                  to={({ item }) => `/doors/${item.door_id}`}
                />
                <Table.Column
                  type="link"
                  title="Equipment Code"
                  dataIndex="container_code"
                  to={({ item }) => {
                    if (
                      item.container_code === "Equipment used for Storage" || 
                      item.container_code === "Equipment used for Pallets"
                    ) {
                      return undefined;
                    }
                    if (isTrailer(item))
                      return `/trailers/${item.container_id}`;
                    if (isContainer(item))
                      return `/containers/${item.container_id}`;
                  }}
                />
                {/* <Table.Column
                  type="text"
                  title="Type"
                  dataIndex="container_type"
                  render={(containerType) => {
                    return EQUIPMENT_TYPE_LABELS[containerType] || "-";
                  }}
                /> */}
                <Table.Column
                  type="text"
                  title="Customer"
                  dataIndex="customer_name"
                  render={(value, item) => {
                    if (isStorageOrPallets(item)) return <Text>-</Text>;
                    return <Text>{value || '-'}</Text>;
                  }}
                />
                <Table.Column
                  type="text"
                  title="Process"
                  dataIndex="direction"
                  render={(direction, item) => {
                    if (isStorageOrPallets(item)) return '-';
                    if (item.door_state === doorState.EMPTY) return '-';
                    if (direction === 'LOAD') return 'Outbound';
                    if (direction === 'UNLOAD') return 'Inbound';
                    return '-';
                  }}
                />
                <Table.Column
                  type="text"
                  title="Method"
                  dataIndex="unload_method"
                  render={(unloadMethod, item) => {
                    if (isStorageOrPallets(item)) return '-';
                    return unloadMethod || '-';
                  }}
                />
                <Table.Column
                  title="Status"
                  dataIndex="door_state"
                  align="center"
                  render={(door_state_value, item) => {
                    if (item?.maintenance_mode === 1) {
                      return <StatusTag status={'MAINTENANCE'} />;
                    }
                    if (isStorageOrPallets(item)) {
                      return <Text>-</Text>;
                    }
                    if (item?.has_cam === 0) {
                      return <StatusTag status={'CAMERA_OFFLINE'} />;
                    }
                    return <StatusTag status={door_state_value} />;
                  }}
                />
                <Table.Column
                  type="datetime"
                  title="Docked"
                  dataIndex="docked_at"
                  tzOffset="timezone_offset"
                  align="right"
                  render={(value, item) => {
                    if (isStorageOrPallets(item)) return <Text>-</Text>;
                    let v = value;
                    if (typeof v === 'number' && v.toString().length === 10) v = v * 1000;
                    const tz = item?.timezone_offset;
                    const text = typeof v === 'number' ? formatDate(v, dateTimeFormat, { tzOffset: tz }) : undefined;
                    return <Text>{text || '-'}</Text>;
                  }}
                />
                <Table.Column
                  title="Total Idle Time"
                  key="idle_time"
                  align="right"
                  render={(item) => {
                    if (isStorageOrPallets(item)) return '-';
                    const { dock_start_iddle, dock_end_iddle } = item;
                    let time = 0;
                    if (
                      dock_start_iddle !== null &&
                      dock_start_iddle !== undefined
                    ) {
                      time += dock_start_iddle;
                    }

                    if (
                      dock_end_iddle !== null &&
                      dock_end_iddle !== undefined
                    ) {
                      time += dock_end_iddle;
                    }

                    return formatDuration(time * 1000, false);
                  }}
                />
                <Table.Column
                  type="datetime"
                  title="Started"
                  dataIndex="started_at"
                  tzOffset="timezone_offset"
                  align="right"
                  render={(value, item) => {
                    if (isStorageOrPallets(item)) return <Text>-</Text>;
                    let v = value;
                    if (typeof v === 'number' && v.toString().length === 10) v = v * 1000;
                    const tz = item?.timezone_offset;
                    const text = typeof v === 'number' ? formatDate(v, dateTimeFormat, { tzOffset: tz }) : undefined;
                    return <Text>{text || '-'}</Text>;
                  }}
                />
                <Table.Column
                  type="duration"
                  title="Unload/Load Time"
                  dataIndex="unload_duration"
                  align="center"
                  withSecs={false}
                  render={(value, item) => {
                    if (isStorageOrPallets(item)) return <Text>-</Text>;
                    if (typeof value !== 'number') return <Text>-</Text>;
                    return <Text>{formatDuration(value * 1000, false)}</Text>;
                  }}
                />
                <Table.Column
                  title="Progress"
                  dataIndex="unloaded_percent"
                  align="center"
                  render={(unloaded_percent, item) => {
                    if (isStorageOrPallets(item)) return "-";
                    if (item.door_state === doorState.EMPTY) return "-";
                    let value = (unloaded_percent * 100).toFixed(0);
                    if (value !== "0" && value !== "100") {
                      value = `${value}`;
                    }
                    return <Tag color="processing">{value}%</Tag>;
                  }}
                />
                <Table.Column
                  title="Speed"
                  dataIndex="relative_speed"
                  align="right"
                  width="110px"
                  render={(
                    speed,
                    item
                  ) => {
                    if (isStorageOrPallets(item)) return "-";
                    if (item.door_state === doorState.EMPTY) return "-";

                    return (
                      <Text
                        style={{
                          textTransform: "capitalize",
                          color: {
                            VERY_SLOW: "#F5222D",
                            SLOW: "#FAAD14",
                            NORMAL: "#52C41A",
                            FAST: "#42B0D5",
                          }[speed],
                        }}
                      >
                        {speed.toLowerCase()}
                      </Text>
                    );
                  }}
                />
                <Table.Column
                  type="number"
                  title="CPH"
                  dataIndex="avg_door_cph"
                  align="right"
                  rounded={true}
                />
                {showIndividuals && (
                  <Table.Column
                    title="Individuals"
                    dataIndex="employees"
                    align="right"
                    width="20px"
                    render={(employees) => {
                      if (!employees || !employees.length) {
                        return <Text style={{ color: colors.primary }}>0</Text>;
                      }

                      return (
                        <Tooltip
                          placement="topRight"
                          title={employees.map((e, i) => (
                            <span key={i}>
                              {e.full_name}
                              <br />
                            </span>
                          ))}
                          trigger="hover"
                        >
                          <Text style={{ color: colors.primary }}>
                            {employees.length}
                          </Text>
                        </Tooltip>
                      );
                    }}
                  />
                )}
              </Table>
            </ViewSwitcher.View>
          </ViewSwitcher>
        </Col>
      </Row>
    </MainLayout>
  );
};

export default Dashboard;
